import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Form, Button, message, Spin } from 'antd';
import type {
  MerchantRegistrationLeadRow,
  RegistrationLeadKycDraft,
} from '../../services/merchantRegistrationLeadsService';
import {
  approveRegistrationLead,
  fetchRegistrationLeadById,
  saveRegistrationLeadKyc,
} from '../../services/merchantRegistrationLeadsService';
import type { LocationPayload } from '../../types/location';
import {
  MerchantRegistrationKycFormContent,
  DEFAULT_PROMO_CREDITS,
} from '../../components/merchants/MerchantRegistrationKycFormContent';

type Props = {
  open: boolean;
  lead: MerchantRegistrationLeadRow | null;
  onClose: () => void;
  onSuccess: () => void;
};

function defaultHoursDays() {
  return Array.from({ length: 7 }, (_, i) => ({
    dayOfWeek: i + 1,
    enabled: true,
    startTime: '08:00',
    endTime: '22:00',
  }));
}

function buildFormValues(
  lead: MerchantRegistrationLeadRow,
  draft?: RegistrationLeadKycDraft | null
) {
  const rep = draft?.representative ?? {};
  return {
    name: draft?.name ?? lead.owner_name ?? '',
    email: draft?.email ?? rep.email ?? '',
    phone: draft?.phone ?? lead.phone ?? '',
    password: draft?.password ?? '',
    address: draft?.address ?? '',
    restaurantName: draft?.restaurantName ?? lead.store_name ?? '',
    restaurantAddress: draft?.restaurantAddress ?? lead.address ?? '',
    latitude: draft?.latitude ?? undefined,
    longitude: draft?.longitude ?? undefined,
    credits: draft?.credits ?? DEFAULT_PROMO_CREDITS,
    provinceName: draft?.provinceName ?? '',
    wardName: draft?.wardName ?? '',
    street: draft?.street ?? '',
    representative: {
      fullName: rep.fullName ?? lead.owner_name ?? '',
      email: rep.email ?? draft?.email ?? '',
      phone: rep.phone ?? draft?.phone ?? lead.phone ?? '',
      idNumber: rep.idNumber ?? '',
      idIssueDate: rep.idIssueDate ?? '',
      idIssuePlace: rep.idIssuePlace ?? '',
      idFrontImageUrl: rep.idFrontImageUrl ?? '',
      idBackImageUrl: rep.idBackImageUrl ?? '',
    },
    bank: {
      bankCode: draft?.bank?.bankCode ?? '',
      bankName: draft?.bank?.bankName ?? '',
      accountNumber: draft?.bank?.accountNumber ?? '',
      accountName: draft?.bank?.accountName ?? '',
      branch: draft?.bank?.branch ?? '',
    },
    photos: {
      avatarUrl: draft?.photos?.avatarUrl ?? '',
      coverUrl: draft?.photos?.coverUrl ?? '',
    },
    hours: {
      days:
        draft?.hours?.days?.length === 7
          ? draft.hours.days
          : defaultHoursDays(),
    },
  };
}

function collectPayload(values: Record<string, unknown>) {
  const locationLat = values.latitude;
  const locationLng = values.longitude;
  return {
    name: String(values.name || '').trim(),
    email: String(values.email || '')
      .trim()
      .toLowerCase(),
    phone: String(values.phone || '').trim(),
    password: values.password ? String(values.password) : undefined,
    address: values.address ? String(values.address).trim() : undefined,
    restaurantName: String(values.restaurantName || '').trim(),
    restaurantAddress: String(values.restaurantAddress || '').trim(),
    latitude:
      locationLat != null && locationLat !== ''
        ? Number(locationLat)
        : undefined,
    longitude:
      locationLng != null && locationLng !== ''
        ? Number(locationLng)
        : undefined,
    credits:
      values.credits != null && values.credits !== ''
        ? Number(values.credits)
        : DEFAULT_PROMO_CREDITS,
    provinceName: values.provinceName
      ? String(values.provinceName).trim()
      : undefined,
    wardName: values.wardName ? String(values.wardName).trim() : undefined,
    street: values.street ? String(values.street).trim() : undefined,
    representative:
      values.representative as RegistrationLeadKycDraft['representative'],
    bank: values.bank as RegistrationLeadKycDraft['bank'],
    photos: values.photos as RegistrationLeadKycDraft['photos'],
    hours: values.hours as RegistrationLeadKycDraft['hours'],
  };
}

export function MerchantRegistrationKycModal({
  open,
  lead,
  onClose,
  onSuccess,
}: Props) {
  const [form] = Form.useForm();
  const [location, setLocation] = useState<LocationPayload | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const submitLockRef = useRef(false);

  const canApprove = true;

  useEffect(() => {
    if (!open || !lead) return;
    let cancelled = false;
    const load = async () => {
      setLoadingDetail(true);
      try {
        const res = await fetchRegistrationLeadById(lead.id);
        if (cancelled) return;
        const detail = res.data ?? lead;
        const draft = detail.kyc_draft;
        const values = buildFormValues(detail, draft);
        form.setFieldsValue(values);
        if (
          values.latitude != null &&
          values.longitude != null &&
          Number.isFinite(Number(values.latitude)) &&
          Number.isFinite(Number(values.longitude))
        ) {
          setLocation({
            lat: Number(values.latitude),
            lng: Number(values.longitude),
            address: values.restaurantAddress || lead.address || '',
          });
        } else {
          setLocation(null);
        }
      } catch {
        if (!cancelled) {
          form.setFieldsValue(buildFormValues(lead, lead.kyc_draft));
          setLocation(null);
        }
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open, lead, form]);

  const handleSave = async () => {
    if (!lead || submitLockRef.current) return;
    submitLockRef.current = true;
    setSaving(true);
    try {
      const values = form.getFieldsValue(true);
      const payload = collectPayload({
        ...values,
        latitude: location?.lat ?? values.latitude,
        longitude: location?.lng ?? values.longitude,
        restaurantAddress:
          location?.address ?? values.restaurantAddress ?? lead.address,
      });
      const res = await saveRegistrationLeadKyc(lead.id, payload);
      if (res.success) {
        message.success('Đã lưu thông tin KYC');
        onSuccess();
      } else {
        message.error(res.message || 'Lưu thất bại');
      }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Lưu thất bại');
    } finally {
      setSaving(false);
      submitLockRef.current = false;
    }
  };

  const handleApprove = async () => {
    if (!lead || submitLockRef.current) return;
    try {
      await form.validateFields();
    } catch {
      message.error('Vui lòng điền đầy đủ thông tin KYC bắt buộc');
      return;
    }

    const values = form.getFieldsValue(true);
    const lat = location?.lat ?? Number(values.latitude);
    const lng = location?.lng ?? Number(values.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      message.error('Vui lòng chọn vị trí quán trên bản đồ');
      return;
    }

    submitLockRef.current = true;
    setApproving(true);
    try {
      const payload = collectPayload({
        ...values,
        latitude: lat,
        longitude: lng,
        restaurantAddress:
          location?.address ?? values.restaurantAddress ?? lead.address,
      });
      const res = await approveRegistrationLead(lead.id, payload);
      if (res.success && res.data) {
        if (res.data.creditWarning) {
          message.warning(`Đã duyệt KYC. ${res.data.creditWarning}`, 8);
        } else {
          message.success(res.message || 'Đã duyệt KYC và tạo quán');
        }
        onSuccess();
        onClose();
      } else {
        message.error(res.message || 'Duyệt thất bại');
      }
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { code?: string; message?: string } };
      };
      const code = ax.response?.data?.code;
      const msg = ax.response?.data?.message;
      if (code === 'PHONE_EXISTS' || code === 'EMAIL_EXISTS') {
        message.error(msg || 'SĐT hoặc email đã tồn tại');
      } else if (code === 'ALREADY_APPROVED') {
        message.warning(msg || 'Lead đã được duyệt');
        onSuccess();
        onClose();
      } else {
        message.error(msg || 'Duyệt thất bại');
      }
    } finally {
      setApproving(false);
      submitLockRef.current = false;
    }
  };

  return (
    <Modal
      title="KYC merchant — đăng ký quán website"
      open={open}
      onCancel={onClose}
      width={760}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose} disabled={saving || approving}>
          Hủy
        </Button>,
        <Button key="save" loading={saving} onClick={() => void handleSave()}>
          Lưu
        </Button>,
        <Button
          key="approve"
          type="primary"
          loading={approving}
          disabled={!canApprove}
          onClick={() => void handleApprove()}
        >
          Duyệt
        </Button>,
      ]}
      styles={{ body: { maxHeight: 'min(78vh, 760px)', overflowY: 'auto' } }}
    >
      {loadingDetail ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : (
        <Form form={form} layout="vertical" requiredMark="optional">
          <MerchantRegistrationKycFormContent
            form={form}
            location={location}
            onLocationChange={setLocation}
            locationPickerKey={lead?.id ?? 'closed'}
            initialAddress={lead?.address ?? ''}
          />
        </Form>
      )}
    </Modal>
  );
}
